/* eslint-disable react-refresh/only-export-components */
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AllTrainingPlans } from '@utils/planTypes';
import { AllDisciplines, AllDistances, AllVolumes, trainingConfig } from '@utils/trainingConfig';
import { getAllTrainingPlans } from '@services/trainingPlanService';

export type TrainingMode = 'race' | 'background' | 'beginner' | null;

interface PlanSelectionContextType {
  trainingMode: TrainingMode;
  setTrainingMode: React.Dispatch<React.SetStateAction<TrainingMode>>;
  selectedDiscipline: AllDisciplines | '';
  setSelectedDiscipline: React.Dispatch<React.SetStateAction<AllDisciplines | ''>>;
  selectedDistance: AllDistances | '';
  setSelectedDistance: React.Dispatch<React.SetStateAction<AllDistances | ''>>;
  selectedVolume: AllVolumes | '';
  setSelectedVolume: React.Dispatch<React.SetStateAction<AllVolumes | ''>>;
  selectedPlan: string | null;
  setSelectedPlan: React.Dispatch<React.SetStateAction<string | null>>;
  selectedStripePlan: string | null;
  setSelectedStripePlan: React.Dispatch<React.SetStateAction<string | null>>;
  recommendation: AllTrainingPlans[];
  setRecommendation: React.Dispatch<React.SetStateAction<AllTrainingPlans[]>>;
  validDisciplines: string[];
  validDistances: AllDistances[];
  validVolumes: AllVolumes[];
  allPlans: AllTrainingPlans[];
  backupPlansList: AllTrainingPlans[];
  currentStepIndex: number;
  setCurrentStepIndex: React.Dispatch<React.SetStateAction<number>>;
  resetPlanSelection: () => void;
}

const PlanSelectionContext = createContext<PlanSelectionContextType | undefined>(undefined);

export const PlanSelectionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [trainingMode, setTrainingMode] = useState<TrainingMode>(null);
  const [selectedDiscipline, setSelectedDiscipline] = useState<AllDisciplines | ''>('');
  const [selectedDistance, setSelectedDistance] = useState<AllDistances | ''>('');
  const [selectedVolume, setSelectedVolume] = useState<AllVolumes | ''>('');
  const [plans, setPlans] = useState<AllTrainingPlans[]>([]);
  const [recommendation, setRecommendation] = useState<AllTrainingPlans[]>([]);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [selectedStripePlan, setSelectedStripePlan] = useState<string | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState<number>(0);

  const backupPlansList = useMemo(
    () => plans.filter(plan => plan.keywords?.includes('GENERAL')),
    [plans]
  );

  const getAllPlans = useCallback(async () => {
    const data = await getAllTrainingPlans();
    setPlans(
      data
        .filter((item: AllTrainingPlans) => item.plan_id != null)
        .map((item: AllTrainingPlans) => ({
          plan_id: item.plan_id as string,
          planName: item.planName,
          keywords: item.keywords,
          weekCount: item.weekCount
        }))
    );
  }, []);

  const planKeywordSets = useMemo(
    () => plans.map(p => new Set((p.keywords ?? []).map(k => k.toUpperCase()))),
    [plans]
  );

  const validDisciplines = useMemo(() => {
    return Object.keys(trainingConfig).filter(discipline =>
      planKeywordSets.some(planSet => planSet.has(discipline.toUpperCase()))
    );
  }, [planKeywordSets]);

  const validDistances = useMemo(() => {
    if (!selectedDiscipline) return [];
    const upperDiscipline = selectedDiscipline.toUpperCase();
    return Object.keys(trainingConfig[selectedDiscipline].distances).filter(dist =>
      planKeywordSets.some(planSet => planSet.has(upperDiscipline) && planSet.has(dist.toUpperCase()))
    ) as AllDistances[];
  }, [selectedDiscipline, planKeywordSets]);

  const validVolumes = useMemo(() => {
    if (!selectedDiscipline || !selectedDistance) return [];
    const discipline = selectedDiscipline as AllDisciplines;
    const distances = trainingConfig[discipline].distances;
    const volumes = (distances as Record<string, readonly string[]>)[selectedDistance] ?? [];
    const upperDiscipline = selectedDiscipline.toUpperCase();
    const upperDistance = selectedDistance.toUpperCase();
    return volumes.filter(volume =>
      planKeywordSets.some(set =>
        set.has(upperDiscipline) && set.has(upperDistance) && set.has(volume.toUpperCase())
      )
    ) as AllVolumes[];
  }, [selectedDiscipline, selectedDistance, planKeywordSets]);

  useEffect(() => {
    getAllPlans();
  }, [getAllPlans]);

  useEffect(() => {
    if (plans.length > 0 && trainingMode === 'race' && selectedDiscipline && selectedDistance && selectedVolume) {
      const upperDiscipline = selectedDiscipline.toUpperCase();
      const upperDistance = selectedDistance.toUpperCase();
      const upperVolume = selectedVolume.toUpperCase();

      const filtered = plans.filter(plan => {
        const keys = plan.keywords ?? [];
        return (
          !keys.includes('GENERAL') &&
          keys.includes(upperDiscipline) &&
          keys.includes(upperDistance) &&
          keys.includes(upperVolume)
        );
      });

      setRecommendation(filtered);

      if (filtered.length > 0) {
        setSelectedPlan(filtered[0].plan_id)
      }

    } else {
      setRecommendation([]);
    }
  }, [plans, trainingMode, selectedDiscipline, selectedDistance, selectedVolume]);

  useEffect(() => {
    if (trainingMode === 'background' && backupPlansList.length > 0) {
      const fallback = backupPlansList.find(plan =>
        plan.keywords?.includes(selectedDistance)
      ) || backupPlansList[0];

      setSelectedPlan(fallback.plan_id);
      setRecommendation([fallback]);
    }
  }, [backupPlansList, trainingMode, plans, selectedDistance]);

  useEffect(() => {
    if (trainingMode === 'beginner' && plans.length > 0) {
      const beginnerPlan = plans.find(plan => {
        const keys = plan.keywords ?? [];
        return keys.some(key => key.toUpperCase() === 'BEGINNER' || key.toUpperCase() === 'DEBUTANT');
      });

      if (beginnerPlan) {
        setSelectedPlan(beginnerPlan.plan_id);
        setRecommendation([beginnerPlan]);
      }
    }
  }, [plans, trainingMode]);

  useEffect(() => {
    setSelectedDistance('');
    setSelectedVolume('');
  }, [selectedDiscipline]);

  useEffect(() => {
    setSelectedVolume('');
  }, [selectedDistance]);

  const resetPlanSelection = useCallback(() => {
    setTrainingMode(null);
    setSelectedDiscipline('');
    setSelectedDistance('');
    setSelectedVolume('');
    setSelectedPlan(null);
    setSelectedStripePlan(null);
    setRecommendation([]);
    setCurrentStepIndex(0);
  }, []);

  const value = useMemo(
    () => ({
      trainingMode,
      setTrainingMode,
      selectedDiscipline,
      setSelectedDiscipline,
      selectedDistance,
      setSelectedDistance,
      selectedVolume,
      setSelectedVolume,
      selectedPlan,
      setSelectedPlan,
      selectedStripePlan,
      setSelectedStripePlan,
      recommendation,
      setRecommendation,
      validDisciplines,
      validDistances,
      validVolumes,
      allPlans: plans,
      backupPlansList,
      currentStepIndex,
      setCurrentStepIndex,
      resetPlanSelection,
    }),
    [
      trainingMode,
      selectedDiscipline,
      selectedDistance,
      selectedVolume,
      selectedPlan,
      selectedStripePlan,
      recommendation,
      validDisciplines,
      validDistances,
      validVolumes,
      plans,
      backupPlansList,
      currentStepIndex,
      resetPlanSelection,
    ]
  );

  return (
    <PlanSelectionContext.Provider value={value}>
      {children}
    </PlanSelectionContext.Provider>
  );
};

export const usePlanSelection = (): PlanSelectionContextType => {
  const context = useContext(PlanSelectionContext);
  if (context === undefined) {
    throw new Error('usePlanSelection must be used within a PlanSelectionProvider');
  }
  return context;
};
